import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Coins,
  Gift,
  Loader2,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import ReferAFriendDialog from "@/components/ReferAFriendDialog";
import {
  disableReferral,
  getMyCredits,
  listMyReferrals,
} from "@/lib/referrals";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
    });
  } catch {
    return iso;
  }
}

/**
 * "Referrals & Credits" card for the student dashboard (Phase 16).
 *
 * ``paidCourses`` is a list of ``{id, title}`` objects — only courses the
 * student has a paid, active enrollment on (the referral endpoint enforces
 * this too, but pre-filtering makes the UX friendlier).
 */
export default function ReferralsCreditsCard({
  studentName,
  paidCourses,
}) {
  const [credits, setCredits] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([getMyCredits(), listMyReferrals()])
      .then(([c, r]) => {
        setCredits(c);
        setReferrals(r);
      })
      .catch(() => {
        // silently degrade — this card is non-blocking
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onDisable = async (id) => {
    try {
      await disableReferral(id);
      toast.success("Link disabled");
      load();
    } catch {
      toast.error("Couldn't disable — try again");
    }
  };

  const totals = credits?.totals || {};
  const totalInr = Number(totals.total_active_inr || 0);
  const totalUsd = Number(totals.total_active_usd || 0);
  const hasCredit = totalInr > 0 || totalUsd > 0;
  const activeCount = referrals.filter((r) => !r.disabled).length;

  return (
    <>
      <Card
        className="border-amber-200 bg-gradient-to-br from-amber-50 to-white"
        data-testid="referrals-credits-card"
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Gift className="h-4 w-4 text-amber-600" />
            Referrals &amp; Credits
          </CardTitle>
          {hasCredit && (
            <Badge
              variant="outline"
              className="bg-emerald-50 text-emerald-800 border-emerald-300"
              data-testid="credit-balance-badge"
            >
              <Coins className="h-3 w-3 mr-1" />
              Available
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="py-3 text-center text-sm text-stone-500">
              <Loader2 className="mx-auto h-4 w-4 animate-spin" />
            </div>
          ) : (
            <>
              <div>
                <div className="text-xs uppercase tracking-wide text-stone-500">
                  Available credit
                </div>
                <div
                  className="text-2xl font-semibold text-stone-900"
                  data-testid="available-credit-amount"
                >
                  ₹{totalInr.toLocaleString()}
                  {totalUsd > 0 && (
                    <span className="text-stone-500 text-base font-normal ml-2">
                      · ${totalUsd.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">
                  Applied automatically at checkout when you opt in.
                </div>
              </div>

              <Button
                onClick={() => setDlgOpen(true)}
                disabled={!paidCourses || paidCourses.length === 0}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="refer-friend-button"
              >
                <Share2 className="mr-1 h-4 w-4" />
                Refer a friend
              </Button>
              {(!paidCourses || paidCourses.length === 0) && (
                <p className="text-xs text-stone-500">
                  Complete payment on a course to unlock referrals.
                </p>
              )}

              {referrals.length > 0 && (
                <div className="border-t border-amber-100 pt-3">
                  <button
                    type="button"
                    onClick={() => setExpanded((s) => !s)}
                    className="w-full flex items-center justify-between text-xs font-medium text-stone-700 hover:text-stone-900"
                    data-testid="my-referrals-toggle"
                  >
                    <span>
                      My referrals ({referrals.length}
                      {activeCount !== referrals.length &&
                        ` · ${activeCount} active`}
                      )
                    </span>
                    {expanded ? (
                      <ChevronUp className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </button>
                  {expanded && (
                    <div
                      className="mt-2 overflow-x-auto border border-stone-200 rounded-md bg-white"
                      data-testid="my-referrals-table"
                    >
                      <table className="w-full text-xs">
                        <thead className="bg-stone-50 text-stone-500">
                          <tr>
                            <th className="text-left py-1.5 px-2">Course</th>
                            <th className="text-right py-1.5 px-2">Uses</th>
                            <th className="text-right py-1.5 px-2">Credited</th>
                            <th className="text-right py-1.5 px-2">Expires</th>
                            <th className="text-right py-1.5 px-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {referrals.map((r) => (
                            <tr
                              key={r.id}
                              className="border-t border-stone-100"
                              data-testid={`referral-row-${r.id}`}
                            >
                              <td className="py-1.5 px-2">
                                <div className="font-medium">
                                  {r.course_title}
                                </div>
                                {r.disabled && (
                                  <Badge
                                    variant="outline"
                                    className="mt-0.5 bg-stone-100 text-stone-600 border-stone-300 text-[10px]"
                                  >
                                    Disabled
                                  </Badge>
                                )}
                              </td>
                              <td className="py-1.5 px-2 text-right">
                                {r.uses_count}
                              </td>
                              <td className="py-1.5 px-2 text-right text-emerald-700 font-medium">
                                {r.credited_count}
                              </td>
                              <td className="py-1.5 px-2 text-right text-stone-500">
                                {fmtDate(r.expires_at)}
                              </td>
                              <td className="py-1.5 px-2 text-right">
                                {!r.disabled && (
                                  <button
                                    type="button"
                                    onClick={() => onDisable(r.id)}
                                    className="text-xs text-red-700 underline hover:no-underline"
                                    data-testid={`disable-referral-${r.id}`}
                                  >
                                    Disable
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ReferAFriendDialog
        open={dlgOpen}
        onOpenChange={setDlgOpen}
        eligibleCourses={paidCourses}
        referrerName={studentName}
        onCreated={() => load()}
      />
    </>
  );
}
